import { type CSSObject } from '@emotion/react';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alpha } from '@mui/material/styles';

import { type InputProps } from './input';
import { type Theme, useTheme } from '../../../theme';

export type TextFieldStylesProps = {
  isFilled?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  contentEditable?: boolean;
  root?: CSSObject;
  size?: InputProps['size'];
};

type TextFieldStyles = {
  root: CSSObject;
  sizeVariant: SizeVariant;
};

type SizeVariant = Record<
  'muiTextFieldRootCSS' | 'endAdornmentIconWrapperCSS',
  CSSObject
>;

const getSizeVariants = (
  theme: Theme,
): Record<NonNullable<InputProps['size']>, SizeVariant> => ({
  regular: {
    muiTextFieldRootCSS: {
      height: 40,
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(1),
      borderRadius: theme.shape.radius8,
      gap: theme.spacing(1),
    },
    endAdornmentIconWrapperCSS: {
      width: 20,
      height: 20,
      marginRight: theme.spacing(2),
    },
  },
  small: {
    muiTextFieldRootCSS: {
      height: 32,
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(0),
      borderRadius: theme.shape.radius8,
      gap: theme.spacing(1),
    },
    endAdornmentIconWrapperCSS: {
      width: 20,
      height: 20,
      marginRight: theme.spacing(2),
    },
  },
});

/*
 * This function provides base styles for many input components.
 * This hook should only be consumed by one input component, and never inherited by other components.
 * This exists to avoid duplication of styles across input components and prevent any inheritance issues.
 */
export const useTextFieldStyles = ({
  isFilled = false,
  hasError = false,
  disabled = false,
  contentEditable = true,
  size = 'small',
}: TextFieldStylesProps): TextFieldStyles => {
  const theme = useTheme();
  const sizeVariant = getSizeVariants(theme)[size];

  return {
    sizeVariant,
    root: {
      [`& .${inputBaseClasses.root}`]: {
        ...sizeVariant.muiTextFieldRootCSS,
        backgroundColor: theme.input.background.default,
        boxShadow: theme.typography.inputShadow,
        caretColor: hasError
          ? theme.input.border.errorActive
          : theme.blinkingCursor.cursor,
        padding: theme.spacing(1),
      },
      [`& .${inputBaseClasses.root}.Mui-focused`]: {
        outlineWidth: theme.spacing(1),
        outlineColor: hasError
          ? theme.input.border.errorFocused
          : theme.input.border.focused,
        outlineStyle: 'solid',
        backgroundColor: theme.input.background.default,
      },
      [`& .${inputBaseClasses.root}.Mui-disabled`]: {
        backgroundColor: theme.input.background.disabled,
        borderColor: theme.border.primary,
        borderWidth: 1,
        borderStyle: 'solid',
      },
      [`& .${inputBaseClasses.input}`]: {
        padding: theme.spacing(0, 1),
        lineHeight: 'normal',
        '&::placeholder': {
          paddingLeft: '1px',
          opacity: 1,
        },
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus':
          {
            WebkitBoxShadow: `0 0 0px 1000px ${theme.input.background.default} inset`,
            transition: 'background-color 5000s ease-in-out 0s',
          },
        [`&.Mui-disabled`]: {
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus':
            {
              WebkitBoxShadow: `0 0 0px 1000px transparent inset`,
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
      },
      [`& .${inputBaseClasses.input}, & .${inputBaseClasses.root}.${outlinedInputClasses.root} textarea`]:
        {
          WebkitTextFillColor: isFilled
            ? theme.text.generic.primary
            : theme.text.interactive.disabled,
          color: isFilled
            ? theme.text.generic.primary
            : theme.text.interactive.disabled,
        },
      [`& .${inputBaseClasses.root}.Mui-disabled .${inputBaseClasses.input}`]: {
        WebkitTextFillColor: Boolean(contentEditable)
          ? alpha(theme.text.interactive.disabled, 0.3)
          : alpha(theme.text.generic.tertiary, 1),
        color: Boolean(contentEditable)
          ? alpha(theme.text.interactive.disabled, 0.3)
          : alpha(theme.text.generic.tertiary, 1),
      },
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.border.primary,
        borderWidth: 1,
      },
      [`& .${inputBaseClasses.root}:hover .${outlinedInputClasses.notchedOutline}`]:
        {
          borderWidth: hasError ? 2 : 1,
          borderColor: Boolean(disabled)
            ? theme.border.primary
            : theme.border.interactive.default,
        },
      [`& .${inputBaseClasses.root}.Mui-focused .${outlinedInputClasses.notchedOutline}`]:
        {
          borderWidth: 2,
          borderColor: theme.border.interactive.default,
        },
      [`& .${inputBaseClasses.root}.Mui-disabled .${outlinedInputClasses.notchedOutline}`]:
        {
          border: 'none',
        },
      [`& .${outlinedInputClasses.root}.Mui-error .${outlinedInputClasses.notchedOutline}`]:
        {
          borderColor: theme.input.border.error,
        },
      [`& .${outlinedInputClasses.root}.Mui-error:hover .${outlinedInputClasses.notchedOutline}`]:
        {
          borderColor: theme.input.border.errorActive,
        },
      [`& .${outlinedInputClasses.root}.Mui-error.Mui-focused .${outlinedInputClasses.notchedOutline}`]:
        {
          borderColor: theme.input.border.errorActive,
        },
    },
  };
};
